import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import React from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { Pagination } from 'antd';
import { Spin } from 'antd';
import { cn } from 'src/components/ui/lib/utils';

interface Props {
  columns: ColumnDef<any, any>[];
  data: any[];
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  filters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  pagination?: {
    page: number;
    pageSize: number;
    totalPage: number;
    onPageChange?: (page: number) => void;
  } | null;
  nodata?: React.ReactNode;
  loading?: boolean;
  expandedRow?: (row: any) => React.ReactNode;
  enableRowSelection?: boolean;
  enableMultiRowSelection?: boolean;
  recordIdField?: string;
  onRowSelection?: OnChangeFn<RowSelectionState>;
  selectedRows?: RowSelectionState;
  fixed?: boolean;
  stickyRow?: React.ReactNode;
}

const CustomTable: React.FC<Props> = ({
  columns,
  data,
  sorting,
  onSortingChange,
  filters,
  onColumnFiltersChange,
  pagination,
  nodata,
  loading,
  expandedRow,
  enableRowSelection,
  enableMultiRowSelection,
  recordIdField,
  onRowSelection,
  selectedRows = [] as any,
  fixed,
  stickyRow,
}) => {
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [expanded, setExpanded] = React.useState({});
  const table = useReactTable({
    data,
    columns,
    onSortingChange,
    onColumnFiltersChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: onRowSelection,
    onExpandedChange: setExpanded,
    getRowCanExpand: () => true, // Allow all rows to expand
    getExpandedRowModel: getExpandedRowModel(),

    getRowId: (row) => (recordIdField ? row[recordIdField] : row.id),
    enableRowSelection,
    enableMultiRowSelection,
    state: {
      sorting,
      columnFilters: filters,
      columnVisibility,
      rowSelection: selectedRows,
      expanded,
    },
  });

  return (
    <div
      className={cn([
        'relative overflow-hidden rounded-md',
        loading ? 'opacity-50 pointer-events-none' : '',
      ])}
    >
      <Table className="table-fixed">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              className="data-[state=selected]:bg-muted border-b transition-colors border-cyan-500/20 hover:bg-cyan-500/5"
              key={headerGroup.id}
            >
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    className="h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] text-blue-600 dark:text-cyan-400 text-xs tracking-widest uppercase"
                    style={{ width: `${header.getSize()}px` }}
                    key={header.id}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {!!stickyRow ? stickyRow : null}
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              return (
                <>
                  <TableRow
                    className="data-[state=selected]:bg-muted border-b border-cyan-500/10 hover:bg-cyan-500/5 transition-all duration-300 group "
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        className="py-[2px] px-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] text-blue-600 dark:text-cyan-100"
                        style={{ width: `${cell.column.getSize()}px` }}
                        key={cell.id}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.getIsExpanded() && <>{expandedRow ? expandedRow(row) : null}</>}
                </>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                {''}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex pt-[20px] justify-end">
        {pagination && pagination.page <= pagination.totalPage && pagination.totalPage > 1 ? (
          <Pagination
            showSizeChanger={false}
            total={pagination.totalPage * pagination.pageSize}
            onChange={pagination.onPageChange}
            current={pagination.page}
            hideOnSinglePage={true}
            pageSize={pagination.pageSize}
            simple
          />
        ) : null}
      </div>
      {loading && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <Spin className="text-tag-yellow-icon" />
        </div>
      )}
    </div>
  );
};

export default CustomTable;
